import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api, { tokenStore } from '@/lib/api';
import { logger } from '@/lib/logger';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';
import { useSessionExpiryWatcher } from '@/lib/useSessionExpiryWatcher';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (err) {
      logger.warn('Auth session check failed:', err?.response?.status || err.message);
      setUser(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  // Mirror the user object into Sentry so every captured event is tied
  // back to the admin who hit the error. No-op when Sentry isn't enabled.
  useEffect(() => {
    if (user) setSentryUser(user); else clearSentryUser();
  }, [user]);

  const login = async (email, password, captchaToken) => {
    const { data } = await api.post('/auth/login', { email, password, cf_turnstile_response: captchaToken });
    if (data.token) tokenStore.setAdmin(data.token);
    setUser(data.user);
    return data;
  };

  const signup = async (email, password, name, societyName, captchaToken) => {
    const { data } = await api.post('/auth/signup', { email, password, name, society_name: societyName, cf_turnstile_response: captchaToken });
    if (!data.requires_email_confirmation) {
      if (data.token) tokenStore.setAdmin(data.token);
      setUser(data.user);
    }
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch (err) { logger.warn('Admin logout request failed:', err.message); }
    tokenStore.clearAdmin();
    api.clearCache?.();
    setUser(null);
    // Caller is expected to navigate via React Router. Avoiding window.location.href
    // here means logout is a smooth client-side route change instead of a full
    // bundle reload (which used to flash the PreviewGate during reload).
  };

  // Hard-cap the session at the JWT's `exp` claim (currently 8h server-side).
  // Without this, the user would only discover the session ended when the
  // next API call returns 401 — landing them in a confusing half-state.
  const onSessionExpire = useCallback(() => {
    logger.info('Admin session expired - auto-logging out.');
    tokenStore.clearAdmin();
    api.clearCache?.();
    setUser(null);
    // Force a route change so any ProtectedRoute kicks in. We use a full
    // navigation here (not React Router) because the timer may fire from
    // any deep page and we want a guaranteed redirect.
    if (typeof window !== 'undefined') window.location.href = '/login';
  }, []);

  useSessionExpiryWatcher({
    token: user ? tokenStore.getAdmin() : null,
    enabled: !!user,
    onExpire: onSessionExpire,
    message: 'Your session has ended after 8 hours. Please sign in again.',
  });

  const isAuthenticated = !!user;

  const value = useMemo(() => ({ user, setUser, loading, login, signup, logout, loadUser, isAuthenticated }), [user, loading, loadUser, isAuthenticated]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
